/* =========================================================================
   useWidgetMenu — custom hook for panel widget context menu and viz options
   ========================================================================= */

import React, { useCallback } from 'react';
import useTelemetry from './useTelemetry';
import { useMenu } from './useMenu';
import { buildVisualizationMenu } from '../lib/telemetry-menu-helpers';
import { TelemetryPanel, MenuItem } from '../lib/dashboard-types';
import { ico, getPanelPrimaryParam } from '../../../../../CommonComponents/telemetry';

import useTelemetryConfirm from './useTelemetryConfirm';

export function useWidgetMenu() {
  const { setConfigPanel, setExpandedTelemetryPanel, removeWidget, patchWidget } = useTelemetry();
  const { confirmWidgetDeletion } = useTelemetryConfirm();
  const { openMenu, closeMenu } = useMenu();

  const openWidgetMenu = useCallback(
    (w: TelemetryPanel, e: React.MouseEvent, atPointer: boolean = false) => {
      const items: MenuItem[] = [];
      if (w.graphMode !== 'kpi') {
        items.push({
          label: 'Configure',
          icon: ico('cfg'),
          onClick: () => {
            setConfigPanel(w);
            closeMenu();
          },
        });
        items.push({
          label: 'Expand',
          icon: ico('expand'),
          onClick: () => {
            setExpandedTelemetryPanel(w);
            closeMenu();
          },
        });
      }
      items.push({
        label: 'Remove',
        icon: ico('trash'),
        danger: true,
        onClick: () => {
          confirmWidgetDeletion(w.name || 'Panel', () => removeWidget(w.id));
          closeMenu();
        },
      });

      openMenu(e, items, { atPointer, width: 120 });
    },
    [openMenu, closeMenu, setConfigPanel, setExpandedTelemetryPanel, removeWidget, confirmWidgetDeletion]
  );

  const handleVizClickMenu = useCallback(
    (w: TelemetryPanel, e: React.MouseEvent, firstSub?: string, firstTel?: string) => {
      const primaryParam = getPanelPrimaryParam(w);
      const sub = primaryParam.subsystem || firstSub || '';
      const tel = primaryParam.paramName || primaryParam.paramId || firstTel || '';
      const items = buildVisualizationMenu(sub, tel, w.graphMode, w.id, patchWidget, closeMenu, primaryParam);
      openMenu(e, items, { width: 140 });
    },
    [openMenu, patchWidget, closeMenu]
  );

  return {
    openWidgetMenu,
    handleVizClickMenu,
  };
}

export default useWidgetMenu;
